import { practicePricingTiers, startingPrice, lowestPlanPrice, highestPlanPrice } from "./pricing";
export const criminalDefensePage = {
  "title": "Criminal Defense Lawyer SEO & Marketing | PageOne",
  "description": "SEO and digital marketing for criminal defense lawyers: rank for every charge you defend, win the map pack and get more signed cases.",
  "intro": [
    "Criminal defense lawyer SEO is the work of getting your firm found on Google, in the local map pack and in AI answers when someone searches for help with a specific charge: a DUI arrest, a drug possession charge, a federal indictment, an assault accusation. Our SEO for criminal defense lawyers is built around those charge-level searches, not generic \"lawyer near me\" traffic, and every page we publish is reviewed against ABA Model Rule 7 and your state bar's advertising rules.",
    "Because SEO takes months to compound, we also run criminal defense marketing that brings cases in now, Google Ads and Local Services Ads, reported on the same dashboard. One program, one strategist, measured in signed cases. If you want the full program across every practice area, see our [law firm SEO services](/services/law-firm-seo)."
  ],
  "byline": "Building search programs for law firms across personal injury, DUI, family and criminal defense.",
  "differences": [
    "**Hundreds of charge types.** Each charge, and each version of it under state and federal law, is its own search. One \"criminal defense\" page can't rank for all of them.",
    "**State vs. federal intent.** Someone searching \"federal drug trafficking lawyer\" needs a different page from someone searching \"first offense possession.\"",
    "**Directories own the broad terms.** Avvo, Nolo, LegalMatch and Justia take top organic spots for broad searches. You beat them on charge-specific and court-specific searches they don't cover well.",
    "**Reputation risk.** Mugshot sites, complaint pages and lead resellers can rank for your firm's own name."
  ],
  "searches": [
    "criminal defense lawyer near me",
    "criminal defense attorneys in [your city]",
    "best criminal defense attorneys",
    "[charge] lawyer [your city], such as \"DUI lawyer Phoenix\" or \"drug charge attorney Houston\"",
    "criminal defense attorney free consultation",
    "experienced criminal defense attorneys",
    "federal criminal defense lawyer",
    "criminal defense law firm [your city]"
  ],
  "includedIntro": "Our criminal law SEO services cover everything a criminal defense firm needs to rank and convert. Each part is done by our team, reviewed by your attorneys for accuracy, and reported monthly.",
  "cards": [
    {
      "title": "Charge-specific practice pages",
      "body": "A dedicated page for each offense you defend in your state: DUI/DWI, drug crimes, assault, theft, domestic violence, weapons, sex crimes, white collar and juvenile. Each page covers the charge, the penalties under your state's law, the defenses you use and what happens next."
    },
    {
      "title": "Federal criminal defense pages",
      "body": "Separate pages for federal practice (white collar, drug trafficking, firearms, fraud) so federal searches land on federal content."
    },
    {
      "title": "Court and county landing pages",
      "body": "Pages for the courts and counties where you appear, so you rank where your clients are actually charged."
    },
    {
      "title": "Google Business Profile and local SEO",
      "body": "Local SEO for criminal defense lawyers: Google Business Profile optimization, categories, posts, citations and a steady review program to win the map pack. See our [local SEO for law firms](/services/local-seo-for-law-firms)."
    },
    {
      "title": "Case-results library",
      "body": "A library of dismissals, reduced charges and verdicts, written to your state bar's rules on past results and disclaimers."
    },
    {
      "title": "Brand and reputation protection",
      "body": "Content and profiles that outrank mugshot sites, complaint pages and lead resellers for your firm's and attorneys' names."
    },
    {
      "title": "Structured data",
      "body": "LegalService, Attorney, Service, BreadcrumbList and FAQPage markup so Google understands your firm, your attorneys and your practice areas."
    },
    {
      "title": "Editorial and legal-directory links",
      "body": "Links from local sponsorships, legal directories and editorial placements, with no link schemes. See our [link building for law firms](/services/link-building-for-law-firms)."
    },
    {
      "title": "AI search visibility",
      "body": "Content structured so your firm can be named in Google AI Overviews, ChatGPT and Perplexity answers about criminal charges in your area. See [AI SEO & GEO](/services/ai-seo-geo)."
    },
    {
      "title": "Bilingual content",
      "body": "Spanish-language charge pages where your market needs them."
    }
  ],
  "charges": [
    [
      "DUI / DWI",
      "\"DUI lawyer [city]\", \"first DUI what happens\"",
      "DUI charge page + court pages (see [DUI/DWI lawyer SEO](/practice-areas/dui-dwi-lawyer-seo))"
    ],
    [
      "Drug crimes",
      "\"drug possession lawyer [city]\", \"federal drug trafficking attorney\"",
      "State drug charge page + federal drug page"
    ],
    [
      "Assault & violent crimes",
      "\"assault charge lawyer\", \"aggravated assault attorney [city]\"",
      "Assault page covering each degree of charge"
    ],
    [
      "Domestic violence",
      "\"domestic violence lawyer near me\"",
      "Domestic violence page + protective order FAQ"
    ],
    [
      "Theft & property",
      "\"shoplifting lawyer [city]\", \"felony theft attorney\"",
      "Theft page by value threshold"
    ],
    [
      "Weapons",
      "\"gun charge lawyer [city]\"",
      "Weapons page (state + federal)"
    ],
    [
      "White collar",
      "\"fraud defense attorney\", \"federal white collar lawyer\"",
      "Federal white-collar page"
    ],
    [
      "Juvenile",
      "\"juvenile defense lawyer [city]\"",
      "Juvenile page written for parents"
    ],
    [
      "Expungement",
      "\"how to expunge a record in [state]\"",
      "Expungement and record-sealing page"
    ]
  ],
  "marketingIntro": "SEO is the channel that compounds, but it takes 60 to 120 days to move. Our criminal defense attorney marketing covers the channels that bring cases while it builds.",
  "marketing": [
    {
      "title": "Google Ads for criminal defense attorneys",
      "body": "Criminal defense attorney PPC campaigns built around charge-level keywords, call-only ads for after-hours searches, and a landing page for each charge. Managed by our [PPC team for law firms](/services/ppc-for-law-firms)."
    },
    {
      "title": "Google Local Services Ads",
      "body": "The \"Google Screened\" ads at the very top of results. You pay per lead, not per click. We handle setup, verification, reviews and lead disputes."
    },
    {
      "title": "Criminal defense advertising that's bar-compliant",
      "body": "Every ad, landing page and disclaimer is reviewed against your state's attorney advertising rules before it goes live. See [attorney advertising disclaimers](/glossary/attorney-advertising-disclaimer)."
    }
  ],
  "comparison": [
    {
      "k": "Cost per click",
      "seo": "$0. You don't pay per visit.",
      "ads": "About $15–$20 average CPC for the main US criminal defense searches*, often higher in big-city markets"
    },
    {
      "k": "Time to first results",
      "seo": "60–120 days",
      "ads": "Within days"
    },
    {
      "k": "When you stop paying",
      "seo": "Rankings don't vanish overnight (they need upkeep)",
      "ads": "Leads stop when spend stops"
    },
    {
      "k": "How searchers see it",
      "seo": "Organic results and the map pack",
      "ads": "Labeled \"Sponsored\""
    },
    {
      "k": "Who owns it",
      "seo": "Your firm owns the pages and rankings",
      "ads": "You rent the placement"
    }
  ],
  "steps": [
    {
      "n": "01",
      "t": "Audit (10 business days).",
      "d": "A technical, on-page, Google Business Profile, backlink and competitor audit of your criminal defense market."
    },
    {
      "n": "02",
      "t": "Roadmap.",
      "d": "A 12-month plan: charge-by-charge keyword map, content calendar, link targets and monthly KPIs tied to signed cases."
    },
    {
      "n": "03",
      "t": "Build.",
      "d": "Charge pages, court pages, schema and speed fixes, with attorney-reviewed content shipped every week."
    },
    {
      "n": "04",
      "t": "Grow.",
      "d": "Ongoing content, editorial links, Google Business Profile posts and review velocity, plus ads where they pay back."
    },
    {
      "n": "05",
      "t": "Report.",
      "d": "Monthly plain-English reporting on rankings, calls, forms, consultations and signed cases, with a quarterly strategy review."
    }
  ],
  "faqs": [
    {
      "q": "How does SEO help criminal defense attorneys get clients?",
      "a": "Most people charged with a crime search for their specific charge and their city before they call anyone. SEO puts your firm in front of those searches, in the map pack and organic results, at the moment they're choosing a lawyer. Unlike ads, the traffic keeps coming without paying for every click."
    },
    {
      "q": "What keywords should a criminal defense lawyer rank for?",
      "a": "Start with charge-plus-location searches (\"DUI lawyer [city]\", \"drug possession attorney [county]\") and \"near me\" searches, then federal and high-value charges. Broad terms like \"criminal lawyer\" are dominated by directories and convert worse. We build the keyword map around the case types that pay."
    },
    {
      "q": "How can criminal defense attorneys use Google Maps to get clients?",
      "a": "Through a fully optimized Google Business Profile: the right primary category, practice-area services, real photos, regular posts and a steady flow of genuine client reviews. For local searches on mobile, the map pack is often the first thing people see."
    },
    {
      "q": "How long does SEO take for a criminal defense firm?",
      "a": "Most firms see early ranking movement in 60 to 120 days and meaningful case growth in 6 to 12 months. It depends on your market's competition, your site's starting point and how many charge pages you need."
    },
    {
      "q": "Should a criminal defense firm use SEO or Google Ads?",
      "a": "Usually both. Google Ads and Local Services Ads bring cases this month; SEO lowers your cost per case over time. We manage both so the budget goes where it brings signed cases."
    },
    {
      "q": "What is the best agency for criminal defense leads?",
      "a": "Look for an agency that works with criminal defense firms specifically, reports on signed cases rather than clicks, reviews content for bar compliance, and doesn't work with your direct competitors. Ask to see real results in a comparable market."
    },
    {
      "q": "How many charge pages do we actually need?",
      "a": "Most firms benefit from 25–60 charge-specific pages, enough to cover real volume without diluting authority. We rank them by your state's actual case data."
    },
    {
      "q": "How long until a criminal defense firm sees ranking movement?",
      "a": "Most criminal defense firms see local pack and long-tail movement in 60–120 days, with primary metro keywords ranking between months 6–12 depending on competition."
    },
    {
      "q": "What contract length do you require?",
      "a": "Engagements are 12 months because SEO compounds over that horizon. There is no auto-renewal, no cancellation penalty at renewal, and month-to-month conversion is available after month 12."
    },
    {
      "q": "Who owns the content, links, and site changes?",
      "a": "You do, from day one. All content, backlinks, tracking, and site changes are the client's property. If we part ways, everything stays with your firm."
    },
    {
      "q": "How does reporting work?",
      "a": "Monthly reports include rankings, organic traffic, tracked calls, form submissions, consultations booked, and signed cases, with a plain-English summary a partner can read in five minutes. Quarterly strategy reviews are led by your senior strategist."
    },
    {
      "q": "What happens if rankings drop after a Google update?",
      "a": "We monitor every core update and roll out a recovery plan within 10 business days, at no extra cost."
    },
    {
      "q": "Do you work with more than one criminal defense firm in the same city?",
      "a": "No. We work with one criminal defense firm per metro market. If we take your criminal defense practice in a city, we won't take a competitor's, that exclusivity is written into every engagement."
    },
    {
      "q": "What's included versus billed as an add-on?",
      "a": "Strategy, technical SEO, on-page, content, GBP, link acquisition, reviews, and reporting are all included in every retainer. Video production, paid media management, and full site rebuilds are quoted separately."
    },
    {
      "q": "Are you bar-compliant?",
      "a": "Yes. Every published asset passes an ABA Model Rule 7 + state bar advertising review, and we keep a compliance log tied to every page and ad."
    }
  ],
  "pricing": practicePricingTiers,
  "serviceSchema": {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Criminal Defense Lawyer SEO & Marketing",
    "serviceType": "Search engine optimization and digital marketing for criminal defense law firms",
    "description": "SEO and digital marketing for criminal defense lawyers: rank for every charge you defend, win the map pack and get more signed cases.",
    "url": "https://pageonelawyers.com/practice-areas/criminal-defense-lawyer-seo",
    "provider": {
      "@type": "Organization",
      "name": "PageOne Lawyers",
      "url": "https://pageonelawyers.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "Criminal defense law firms and attorneys"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": lowestPlanPrice,
      "highPrice": highestPlanPrice,
      "offerCount": "6",
      "url": "https://pageonelawyers.com/pricing"
    }
  },
  "breadcrumbSchema": {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://pageonelawyers.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Practice Areas",
        "item": "https://pageonelawyers.com/practice-areas"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Criminal Defense Lawyer SEO",
        "item": "https://pageonelawyers.com/practice-areas/criminal-defense-lawyer-seo"
      }
    ]
  },
  "related": [
    {
      "anchor": "DUI/DWI lawyer SEO",
      "target": "/practice-areas/dui-dwi-lawyer-seo"
    },
    {
      "anchor": "How DUI defense firms win local search",
      "target": "/blog/dui-lawyer-seo-guide"
    },
    {
      "anchor": "Keyword research for law firms",
      "target": "/blog/keyword-research-for-law-firms"
    },
    {
      "anchor": "How law firms turn SEO traffic into new clients",
      "target": "/blog/how-law-firms-turn-seo-traffic-into-new-clients"
    },
    {
      "anchor": "Phoenix DUI defense case study",
      "target": "/case-studies/dui-defense-phoenix"
    }
  ],
  "caseChallenge": "A solo DUI defense attorney was spending $14,000 a month on Google Ads with falling returns. Two aggregator sites and a national brand held the top three positions for every DUI search in the market, and mugshot and arrest-record sites ranked for the attorney's own name.",
  "caseApproach": [
    "Built dedicated pages for first-offense DUI, felony DUI, refusal/implied consent, CDL DUI and extreme DUI",
    "Optimized the Google Business Profile for after-hours intake with 24/7 messaging and category tuning",
    "Ran a brand-protection campaign to push mugshot and arrest-record pages off page one for the attorney's name",
    "Published a monthly DUI case-results page with structured data",
    "Earned links from Arizona bar-association resources and local recovery non-profits"
  ],
  "caseResults": "Within 5 months the firm held the #1 local pack position for \"DUI lawyer Phoenix\" and top-3 for every modifier search. After-hours mobile calls grew 417%, paid spend dropped 60% while total case volume rose, and organic now drives 82% of new leads.",
  "pricingIntro": `Plans start at ${startingPrice}/month, with no setup fees and no surprise add-ons. Every plan includes attorney-reviewed content and full transparency on every deliverable. The right plan depends on how many charge pages, locations and markets you need to cover.`,
  "why": [
    "**One firm per market.** We don't work with competing criminal defense firms in the same city.",
    "**Bar-compliant by default.** Every page and ad is reviewed against ABA Model Rule 7 and your state bar's advertising rules.",
    "**Attorney-reviewed content.** Your attorneys approve every charge page for legal accuracy before it goes live.",
    "**Measured in signed cases.** We report calls, consultations and signed cases, not just rankings.",
    "**You own everything.** Content, links and site changes stay with your firm if you leave."
  ]
};
